import React, { useState, useEffect } from 'react';
import { Drawer, Box, Typography, IconButton, Button, Stack, FormControlLabel, Switch, lighten } from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import {
  DepartmentSelect,
  RegionSelect,
  DepotSelect,
  UserSelect,
  AgeRangeSelect,
  ShiftPatternSelect,
  SexSelect,
  JobRoleSelect,
} from './Filters/components';
import { useDepots, useJobRoles } from './Filters/hooks';
import { clearDownstream, upstreamFor } from './Filters/filterCascade';
import { useClientScope } from '../contexts';

export interface FilterState {
  region: string[];
  depot: string[];
  department: string[];
  jobRole: string[];
  searchUser: string[];
  ageRange: string[];
  sex: string[];
  shiftPattern: string[];
  /**
   * "Reliable data only" (BT-101). Restricts every dashboard to participants who
   * wore their device for at least 80% of the selected range, and — when a
   * comparison period is active — who have data in both periods.
   */
  reliableDataOnly: boolean;
}

export interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterState) => void;
  initialFilters?: Partial<FilterState>;
  hideUserSelect?: boolean; // Option to hide user selection (for pages with dedicated user selector)
  hideDepartmentSelect?: boolean; // Option to hide department selection (for pages with dedicated department selector)
  hideJobRoleSelect?: boolean;
  /**
   * z-index override. Needed when this drawer opens on top of another modal
   * (e.g. the report generation wizard Dialog) — the shared default (1300)
   * collides with the parent Dialog, which then blocks interaction. Pass a
   * value above the parent's z-index to make the drawer usable.
   */
  zIndex?: number;
}

const DEFAULT_FILTERS: FilterState = {
  region: [],
  depot: [],
  department: [],
  jobRole: [],
  searchUser: [],
  ageRange: [],
  sex: [],
  shiftPattern: [],
  reliableDataOnly: false,
};

export const getAppliedFilterCount = (filters?: Partial<FilterState> | null): number => {
  if (!filters) return 0;

  const keys: Array<keyof FilterState> = [
    'region',
    'depot',
    'department',
    'jobRole',
    'searchUser',
    'ageRange',
    'sex',
    'shiftPattern',
  ];

  const arrayCount = keys.reduce((count, key) => {
    const value = filters[key];
    return Array.isArray(value) && value.length > 0 ? count + 1 : count;
  }, 0);

  // Counted like any other applied filter so the badge reflects that results
  // are narrowed — this one is easy to leave on and forget.
  return arrayCount + (filters.reliableDataOnly ? 1 : 0);
};

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  onApply,
  initialFilters,
  hideUserSelect = false,
  hideDepartmentSelect = false,
  hideJobRoleSelect = false,
  zIndex,
}) => {
  const { isAnonymised } = useClientScope();
  const depots = useDepots();
  const hasDepots = (depots.data?.data?.length ?? 0) > 0;
  const [filters, setFilters] = useState<FilterState>(DEFAULT_FILTERS);
  const jobRoles = useJobRoles({
    regionIds: filters.region,
    depotIds: filters.depot,
    departmentIds: filters.department,
  });
  const hasJobRoles = (jobRoles.data?.data?.length ?? 0) > 0;

  const updateFilter = (level: Parameters<typeof clearDownstream>[1], values: string[]) => {
    setFilters((prev) => clearDownstream(prev, level, values));
  };
  const upstream = upstreamFor(filters);

  useEffect(() => {
    if (open) {
      setFilters({ ...DEFAULT_FILTERS, ...initialFilters });
    }
  }, [open, initialFilters]);

  const handleReset = () => {
    setFilters(DEFAULT_FILTERS);
  };

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={zIndex !== undefined ? { zIndex } : undefined}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '500px',
        },
      }}
    >
      {/* Header */}
      <Box
        display='flex'
        justifyContent='space-between'
        alignItems='flex-start'
        sx={{
          p: 2,
          backgroundColor: (theme) => lighten(theme.palette.background.paper, 0.1),
        }}
      >
        <Box>
          <Typography variant='body1' gutterBottom>
            Filter Results
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Please use following filters to refine results
          </Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Stack spacing={3} sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 3 }}>
        <RegionSelect value={filters.region} onChange={(values) => updateFilter('region', values)} />

        {hasDepots && (
          <DepotSelect
            value={filters.depot}
            onChange={(values) => updateFilter('depot', values)}
            regionIds={upstream.depot.regionIds}
          />
        )}

        {!hideDepartmentSelect && (
          <DepartmentSelect
            value={filters.department}
            onChange={(values) => updateFilter('department', values)}
            regionIds={filters.region}
          />
        )}

        {!hideJobRoleSelect && hasJobRoles && (
          <JobRoleSelect
            value={filters.jobRole}
            onChange={(values) => updateFilter('jobRole', values)}
            regionIds={upstream.jobRole.regionIds}
            depotIds={upstream.jobRole.depotIds}
            departmentIds={upstream.jobRole.departmentIds}
          />
        )}

        <AgeRangeSelect
          value={filters.ageRange}
          onChange={(values) => updateFilter('ageRange', values)}
          {...upstream.ageSexShift}
        />

        <SexSelect value={filters.sex} onChange={(values) => updateFilter('sex', values)} {...upstream.ageSexShift} />

        <ShiftPatternSelect
          value={filters.shiftPattern}
          onChange={(values) => updateFilter('shiftPattern', values)}
          {...upstream.ageSexShift}
        />

        {!hideUserSelect && !isAnonymised && (
          <UserSelect
            value={filters.searchUser}
            onChange={(userIds) => updateFilter('searchUser', userIds)}
            {...upstream.user}
          />
        )}

        <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
          <FormControlLabel
            control={
              <Switch
                checked={filters.reliableDataOnly}
                onChange={(e) => setFilters({ ...filters, reliableDataOnly: e.target.checked })}
              />
            }
            label='Reliable data only'
          />
          <Typography variant='body2' color='text.secondary'>
            Limits results to participants who wore their device for at least 80% of the selected period. When comparing
            two periods, also requires data in both.
          </Typography>
        </Box>
      </Stack>

      {/* Footer Actions */}
      <Box
        display='flex'
        gap={2}
        mt='auto'
        sx={{
          justifyContent: 'flex-end',
          p: 3,
          borderTop: '1px solid',
          borderColor: 'divider',
          backgroundColor: (theme) => lighten(theme.palette.background.paper, 0.1),
        }}
      >
        <Button variant='outlined' startIcon={<RotateLeftIcon />} onClick={handleReset}>
          RESET
        </Button>
        <Button variant='contained' startIcon={<CheckIcon />} onClick={handleApply}>
          APPLY
        </Button>
      </Box>
    </Drawer>
  );
};
